import { PractitionerPreviewContent } from '~/app/pages/shared/Practitioner/PractitionerPreview'
import { AstraCallout, AstraHoverCard, isFutureMonth } from '~/astra'
import { useOpenDialog } from '~/components/DialogRouter/useDialogRouter'
import { useFlag } from '~/hooks/useFlag'
import { useStore } from '~/store/store'
import { Practitioner, usePractitioner } from '~/storm'

import { usePractitionerPredictedWaitingList } from '../usePractitionerPredictedWaitingList'

type Props = {
    practitioner: Practitioner
}

const PractitionerRowWithWaitingListIndicator = ({ practitioner }: Props) => {
    const openDialog = useOpenDialog('practitioner')
    const { isLoading, isPredictedBelowSafeLevels, waitingListEndOfMonth } = usePractitionerPredictedWaitingList(practitioner)
    const showWarning = !isLoading && isPredictedBelowSafeLevels
    const warningBackgroundStyle = showWarning ? 'bg-caution-subtle-rest' : 'group-hover:bg-surface-strong'

    return (
        <>
            <AstraHoverCard
                noPadding
                placement="right"
                hoverContent={
                    <PractitionerPreviewContent practitioner={practitioner}>
                        {showWarning && (
                            <AstraCallout
                                color="caution"
                                text={`For få pasienter (${waitingListEndOfMonth}) på predikert venteliste til å fylle alle operasjonsdager.`}
                                size="small"
                            />
                        )}
                    </PractitionerPreviewContent>
                }
            >
                <div
                    className={`flex h-full w-full cursor-pointer items-center gap-1.5 px-2 ${warningBackgroundStyle}`}
                    onClick={() => openDialog({ practitioner })}
                >
                    {showWarning && <div className="h-full max-h-6 w-[3px] shrink-0 rounded-full bg-caution-strong-rest" />}
                    <p className={`rounded py-1 transition`}>{practitioner.short_name}</p>
                </div>
            </AstraHoverCard>
        </>
    )
}

const OldPractitionerRow = ({ practitioner }: Props) => {
    const open = useOpenDialog('practitioner')
    return (
        <>
            <AstraHoverCard noPadding placement="right" hoverContent={<PractitionerPreviewContent practitioner={practitioner} />}>
                <span className="w-full" onClick={() => open({ practitioner })}>
                    <p className={`w-full cursor-pointer rounded px-2 py-1 transition group-hover:bg-surface-strong`}>{practitioner.short_name}</p>
                </span>
            </AstraHoverCard>
        </>
    )
}

// The new PractitionerRow with predicted waiting list indicator is put behind a feature flag because I'm not sure how performance will be in production with real DIPS data
// Since I can't conditionally call the usePractitionerPredictedWaitingList hook I did it like this instead, with conditionally rendering PractitionerRow either with or without the predicted waiting list
// This is temporary until we get to test performance at LDS
export const PractitionerRow = (props: { practitionerUuid: Practitioner['uuid'] }) => {
    const practitioner = usePractitioner({ where: { uuid: props.practitionerUuid } })
    const isLowPredictedWaitingListIndicator = useFlag('surgeon_scheduler__low_predicted_waiting_list')
    const selectedDate = useStore(state => state.appFilters.selectedDate)
    const isSelectedDateInFuture = isFutureMonth(selectedDate)

    if (!practitioner) return null

    return isLowPredictedWaitingListIndicator && isSelectedDateInFuture ? (
        <PractitionerRowWithWaitingListIndicator practitioner={practitioner} />
    ) : (
        <OldPractitionerRow practitioner={practitioner} />
    )
}
